import { STORAGE_KEYS } from '../constants/api';

// Интерфейс для JWT payload
interface JWTPayload {
  exp: number;
  iat: number;
  sub: string;
  [key: string]: any;
}

// Функция для декодирования JWT токена
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

// Функция для проверки срока действия токена
export const isTokenExpired = (token: string, bufferMinutes: number = 5): boolean => {
  const payload = decodeJWT(token);
  if (!payload) return true;

  const currentTime = Date.now() / 1000;
  const expirationTime = payload.exp;
  const bufferTime = bufferMinutes * 60; // конвертируем минуты в секунды

  return expirationTime < (currentTime + bufferTime);
};

// Функция для получения времени истечения токена
export const getTokenExpirationTime = (token: string): Date | null => {
  const payload = decodeJWT(token);
  if (!payload) return null;

  return new Date(payload.exp * 1000);
};

// Функция для получения оставшегося времени жизни токена в минутах
export const getTokenTimeToLive = (token: string): number => {
  const payload = decodeJWT(token);
  if (!payload) return 0;

  const currentTime = Date.now() / 1000;
  const timeToLive = payload.exp - currentTime;

  return Math.max(0, Math.floor(timeToLive / 60)); // возвращаем в минутах
};

// Функция для получения информации о токене
export const getTokenInfo = (token: string) => {
  const payload = decodeJWT(token);
  if (!payload) return null;

  const currentTime = Date.now() / 1000;
  const expirationTime = payload.exp;
  const timeToLive = expirationTime - currentTime;

  return {
    issuedAt: new Date(payload.iat * 1000),
    expiresAt: new Date(expirationTime * 1000),
    timeToLive: Math.max(0, timeToLive),
    timeToLiveMinutes: Math.max(0, Math.floor(timeToLive / 60)),
    isExpired: timeToLive <= 0,
    subject: payload.sub,
    payload,
  };
};

// Функция для проверки валидности токена
export const isTokenValid = (token: string): boolean => {
  if (!token) return false;
  
  try {
    const payload = decodeJWT(token);
    if (!payload) return false;

    const currentTime = Date.now() / 1000;
    return payload.exp > currentTime;
  } catch (error) {
    return false;
  }
};

// Функция для получения токенов из localStorage
export const getStoredTokens = () => {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

  return {
    accessToken,
    refreshToken,
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!refreshToken,
    isAccessTokenValid: accessToken ? isTokenValid(accessToken) : false,
    isRefreshTokenValid: refreshToken ? isTokenValid(refreshToken) : false,
  };
};

// Функция для очистки токенов из localStorage
export const clearStoredTokens = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
};

// Функция для форматирования времени
export const formatTimeRemaining = (minutes: number): string => {
  if (minutes <= 0) return 'Истек';
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours > 0) {
    return `${hours}ч ${remainingMinutes}м`;
  }
  
  return `${remainingMinutes}м`;
};
